import React, { useEffect, useRef, useState } from 'react';
import { ScrollView, View, TouchableOpacity, Modal, ActivityIndicator } from 'react-native';
import { translate } from '../../../services/translate';
import MyAppText from '../../MyAppText';
import { faLocationDot, faSearchPlus, faTimes, faPlay, faChevronUp, faChevronDown, IconDefinition, faAnglesLeft, faAngleLeft, faAngleRight, faAnglesRight } from '@fortawesome/free-solid-svg-icons';
import { faCalendarCheck } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import OccurrenceVideoModal from './OccurrenceVideoModal';
import { dispatchService } from '../../../services/central-api/dispatch';
import { Hoverable } from 'react-native-web-hover';
import { WindowInformation } from '../../../services/window-information';
import { faCameraCctv } from '@fortawesome/pro-solid-svg-icons';
import CustomButton from '../../CustomButton';
import DragAndZoom from '../../DragAndZoom';
import Toast from 'react-native-toast-message';
import { RootNavigation } from '../../../Routes';
import Link from '../../Link';
import { openInNewTab } from '../../../services/navigation-assistant';

export default function OccurrencePeopleCrowd({
    occurrence, navigation
}: {
    occurrence: Occurrence;
    navigation: RootNavigation;
}) {
    const { styles, theme } = useStyles(stylesheet);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const modalCanvasRef = useRef<HTMLCanvasElement>(null);
    const [isVideoModalVisible, setVideoModalVisible] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const windowInfo = WindowInformation();
    const [currentImage, setCurrentImage] = useState<{ imageUrl: string; }>({ imageUrl: '' });
    const [showDetails, setShowDetails] = useState<boolean>(false);
    const [isImageLoading, setIsImageLoading] = useState<boolean>(false);
    const [currentImagePage, setCurrentImagePage] = useState(0);

    if (occurrence.triggerType != 'people_crowd') {
        return <></>;
    }

    function drawDetections(
        canvas: HTMLCanvasElement,
        imageUrl: string,
        options?: { showConfidence?: boolean; onLoadEnd?: () => void; }
    ) {
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return;
        }

        const image = new Image();

        image.onload = () => {
            canvas.width = image.width;
            canvas.height = image.height;
            ctx.drawImage(image, 0, 0);
            options?.onLoadEnd?.();
        };

        image.onerror = () => {
            options?.onLoadEnd?.();
        };

        image.src = imageUrl;

        return () => {
            image.src = '';
        };
    }

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) {
            return;
        }

        return drawDetections(
            canvas,
            occurrence.resume.imageUrl,
        );
    }, [occurrence]);

    useEffect(() => {
        if (!isModalVisible) {
            return;
        }
        const canvas = modalCanvasRef.current;
        if (!canvas) {
            return;
        }

        setIsImageLoading(true);

        return drawDetections(
            canvas,
            currentImage.imageUrl,
        );
    }, [currentImage, isModalVisible]);

    async function getOccurrenceImage(occurrenceImageId: number) {
        try {
            const occurrenceImage = await dispatchService.getOccurrenceImageSasToken(occurrence.id ?? 0, occurrenceImageId);
            setCurrentImage({ imageUrl: occurrenceImage.resume.imageUrl });
            setIsModalVisible(true);
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    return (
        <>
            <ScrollView contentContainerStyle={{ rowGap: 10 }}>
                <Hoverable>
                    {({ hovered }) => (
                        <TouchableOpacity
                            style={{ flex: 1 }}
                            onPress={async () => await getOccurrenceImage(occurrence.occurrenceImages[currentImagePage].id)}
                        >
                            <canvas style={{ width: '100%' }} ref={canvasRef} />

                            {hovered &&
                                <View style={{
                                    position: 'absolute',
                                    width: '100%',
                                    height: '100%',
                                    backgroundColor: '#00000080',
                                    justifyContent: 'center',
                                }}>
                                    <FontAwesomeIcon icon={faSearchPlus} fontSize={28} color={'#FFFFFF'} />
                                </View>
                            }
                        </TouchableOpacity>
                    )}
                </Hoverable>

                <View style={styles.generalInformation}>
                    <TouchableOpacity
                        style={styles.generalInformationDropdown}
                        onPress={() => setShowDetails(!showDetails)}>
                        <MyAppText>{translate('general_info')}</MyAppText>
                        <FontAwesomeIcon icon={showDetails ? faChevronUp : faChevronDown} color={theme.colors.iconColor} />
                    </TouchableOpacity>
                    <View style={styles.tableLine}></View>
                </View>

                {showDetails ? <View style={{ gap: 5 }}>
                    <MyAppText style={styles.plateText}>
                        {translate('visualizedOn')}
                    </MyAppText>
                    <Link
                        onPress={() => setVideoModalVisible(true)}
                        icon={faPlay}
                        text={translate('watchVideo')}
                        textColor={theme.colors.labelColor}
                    />
                    <Link
                        onPress={() => {
                            if (!occurrence.externalReference?.id) {
                                return;
                            }
                            navigation.navigate('Cameras', { screen: 'CameraPlayer', params: { id: occurrence.externalReference.id } });
                        }}
                        onMiddleClick={() => {
                            if (!occurrence.externalReference?.id) {
                                return;
                            }
                            openInNewTab('Cameras', { screen: 'CameraPlayer', params: { id: occurrence.externalReference.id } });
                        }}
                        icon={faCameraCctv as IconDefinition}
                        text={occurrence.externalReference?.title ?? ''}
                        textColor={theme.colors.labelColor}
                        fontSize={12}
                    />
                    <View style={styles.visualizedRow} >
                        <FontAwesomeIcon icon={faCalendarCheck} style={{ width: 14 }} color={theme.colors.iconColor} />
                        <MyAppText style={styles.detectionInfoText}>
                            {moment(new Date(occurrence.resume.time)).format('DD/MM/YYYY HH:mm:ss')}
                        </MyAppText>
                    </View>
                    <View style={styles.visualizedRow} >
                        <FontAwesomeIcon icon={faLocationDot} style={{ width: 14 }} color={theme.colors.iconColor} />
                        <MyAppText style={styles.detectionInfoText}>
                            {occurrence.externalReference?.address}
                        </MyAppText>
                    </View>
                </View> : <></>}
                <OccurrenceVideoModal
                    occurrenceId={occurrence.id || 0}
                    isModalVisible={isVideoModalVisible}
                    setModalVisible={setVideoModalVisible}
                    occurrenceTime={new Date(occurrence.resume.time).getTime()}
                    apiService={dispatchService}
                />

                {/* Zoom image modal */}
                <Modal
                    animationType='fade'
                    transparent={true}
                    visible={isModalVisible}
                    onRequestClose={() => setIsModalVisible(false)}>
                    <View style={styles.modalContainer}>
                        <View style={styles.centeredView}>
                            <View style={[styles.modalBody, windowInfo.isMobile ? { width: '95%' } : { width: '60%' }]}>
                                <View style={{ alignItems: 'flex-end' }}>
                                    <TouchableOpacity
                                        style={{ width: 20, height: 20 }}
                                        onPress={() => setIsModalVisible(false)}
                                    >
                                        <FontAwesomeIcon icon={faTimes} style={{ width: 20 }} color={theme.colors.iconColor} />
                                    </TouchableOpacity>
                                </View>

                                <View style={{ flexDirection: 'row', gap: 5 }}>
                                    {isImageLoading && <ActivityIndicator style={[{ width: '100%', height: '100%' }, windowInfo.isMobile ? { minHeight: 150 } : { minHeight: 450 }]} size='large' color={theme.colors.loadingColor} />}
                                    <DragAndZoom>
                                        <canvas style={isImageLoading ? { width: '100%', opacity: 0 } : { width: '100%', opacity: 1 }} ref={modalCanvasRef} />
                                    </DragAndZoom>
                                </View>

                                <View style={{ flexDirection: 'row', gap: 5, justifyContent: 'center' }}>
                                    <CustomButton
                                        type='secondary'
                                        onPress={async () => {
                                            if (currentImagePage <= 0) {
                                                return;
                                            }
                                            setCurrentImagePage(0);
                                            await getOccurrenceImage(occurrence.occurrenceImages[0].id);
                                        }}
                                        disabled={currentImagePage <= 0}
                                        icon={faAnglesLeft}
                                    />
                                    <CustomButton
                                        type='secondary'
                                        onPress={async () => {
                                            if (currentImagePage <= 0) {
                                                return;
                                            }

                                            const previousPage = currentImagePage - 1;
                                            setCurrentImagePage(previousPage);
                                            await getOccurrenceImage(occurrence.occurrenceImages[previousPage].id);
                                        }}
                                        disabled={currentImagePage <= 0}
                                        icon={faAngleLeft}
                                    />
                                    <MyAppText style={{ alignContent: 'center', textAlign: 'center', paddingHorizontal: 10 }}>{currentImagePage + 1}/{occurrence.occurrenceImages.length}</MyAppText>
                                    <CustomButton
                                        type='secondary'
                                        onPress={async () => {
                                            if (currentImagePage >= occurrence.occurrenceImages.length - 1) {
                                                return;
                                            }
                                            const nextPage = currentImagePage + 1;
                                            setCurrentImagePage(nextPage);
                                            await getOccurrenceImage(occurrence.occurrenceImages[nextPage].id);
                                        }}
                                        disabled={currentImagePage >= occurrence.occurrenceImages.length - 1}
                                        icon={faAngleRight}
                                    />
                                    <CustomButton
                                        type='secondary'
                                        onPress={async () => {
                                            const lastPosition = occurrence.occurrenceImages.length - 1;
                                            if (currentImagePage >= lastPosition) {
                                                return;
                                            }
                                            setCurrentImagePage(lastPosition);
                                            await getOccurrenceImage(occurrence.occurrenceImages[lastPosition].id);
                                        }}
                                        disabled={currentImagePage >= occurrence.occurrenceImages.length - 1}
                                        icon={faAnglesRight}
                                    />
                                </View>
                            </View>
                        </View>
                    </View>
                </Modal>
            </ScrollView>
            {occurrence.occurrenceImages.length > 1 ?
                <MyAppText style={{
                    position: 'absolute',
                    top: 5,
                    right: 5,
                    backgroundColor: '#D50000',
                    color: '#FFFFFF',
                    borderRadius: 10,
                    minWidth: 20,
                    height: 20,
                    paddingHorizontal: 3,
                    textAlign: 'center',
                }}>{occurrence.occurrenceImages.length}</MyAppText>
                : null}
        </>
    );
}

const stylesheet = createStyleSheet(theme => ({
    container: {
        padding: 16,
        backgroundColor: theme.colors.container.background,
        borderRadius: 8,
        borderColor: theme.colors.container.border,
        borderWidth: 1,
    },
    detectionInfoText: {
        color: theme.colors.labelColor,
        fontSize: 12
    },
    modalContainer: {
        flex: 1,
        backgroundColor: '#31313199',
    },
    centeredView: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    modalBody: {
        backgroundColor: theme.colors.container.background,
        flexDirection: 'column',
        borderRadius: 8,
        rowGap: 20,
        padding: 30,
    },
    tableLine: {
        borderBottomColor: theme.colors.card.border,
        borderBottomWidth: 1,
        flex: 1
    },
    generalInformation: {
        flexDirection: 'column',
        paddingHorizontal: 5,
        flex: 1,
        gap: 10
    },
    generalInformationDropdown: {
        flexDirection: 'row',
        gap: 10,
        alignItems: 'center',
        justifyContent: 'space-between',
        flex: 1
    },
    plateText: {
        fontWeight: 'bold',
        fontSize: 14
    },
    visualizedRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5
    },
}));
